/**
 * EazeLeadCard — the growth moment on the renew success screen.
 *
 * The member just paid via a smooth link — so we sell that exact feeling.
 * Progressive, low-friction, dismissible: tap "Yes, I run a business" (a
 * complete lead — contact comes from their membership, no typing), then
 * optionally tap a business-type chip to enrich it. Each tap commits; nothing
 * is required. Posts to the public /renew/:token/lead endpoint.
 */
import React, { useState } from "react";
import { Sparkles, ArrowRight, CheckCircle2, X } from "lucide-react";

interface Props {
  token: string;
  apiBase: string;
}

type Phase = "pitch" | "survey" | "done" | "hidden";

const TYPES: Array<{ key: string; label: string }> = [
  { key: "shop", label: "Shop / Retail" },
  { key: "food", label: "Food" },
  { key: "services", label: "Services" },
  { key: "other", label: "Other" },
];

const EazeLeadCard: React.FC<Props> = ({ token, apiBase }) => {
  const [phase, setPhase] = useState<Phase>("pitch");
  const [busy, setBusy] = useState(false);

  const post = async (businessType?: string) => {
    try {
      await fetch(`${apiBase}/renew/${token}/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(businessType ? { businessType } : {}),
      });
    } catch {
      /* non-blocking — a missed lead shouldn't disrupt the member */
    }
  };

  const sayYes = async () => {
    setBusy(true);
    await post(); // capture the lead immediately
    setBusy(false);
    setPhase("survey");
  };

  const pickType = async (key: string) => {
    setBusy(true);
    await post(key); // enrich the same lead
    setBusy(false);
    setPhase("done");
  };

  if (phase === "hidden") return null;

  return (
    <div className="mt-5 w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-left">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#4CAF50]">
          <Sparkles size={13} />
          Powered by Eaze
        </div>
        {phase !== "done" && (
          <button
            type="button"
            onClick={() => setPhase("hidden")}
            className="text-slate-300 hover:text-slate-500"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {phase === "pitch" && (
        <>
          <p className="text-sm font-semibold text-slate-900">
            Run a business? Get paid like this.
          </p>
          <p className="mt-1 text-sm text-slate-600">
            That smooth payment you just made — Eaze gives your business the
            same. Sales, stock, and pay-by-link for your customers.
          </p>
          <button
            type="button"
            onClick={sayYes}
            disabled={busy}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            Yes, I run a business
            <ArrowRight size={14} />
          </button>
        </>
      )}

      {phase === "survey" && (
        <>
          <p className="text-sm font-medium text-slate-900">What kind?</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => pickType(t.key)}
                disabled={busy}
                className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-[#4CAF50] hover:text-[#4CAF50] disabled:opacity-50"
              >
                {t.label}
              </button>
            ))}
          </div>
        </>
      )}

      {phase === "done" && (
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} className="text-[#4CAF50]" />
          <p className="text-sm text-slate-700">
            Nice — the Eaze team will reach out.
          </p>
        </div>
      )}
    </div>
  );
};

export default EazeLeadCard;
